/**
 * Session and identity hooks.
 *
 * The token lives in localStorage so a reload keeps the planner signed in; in
 * a Captura deployment the token arrives from SSO instead and this is the only
 * file that changes.
 */

import { useCallback, useEffect, useState } from 'react'
import { api, getToken, setToken, setTenantOverride } from '../lib/api'
import type { HealthResponse, MeResponse } from '../lib/types'

export function useIdentity() {
  const [me, setMe] = useState<MeResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>(null)

  const load = useCallback(async () => {
    if (!getToken()) {
      setMe(null)
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      setMe(await api.me())
      setError(null)
    } catch (err) {
      setError(err)
      setMe(null)
      // A stale or revoked token should not wedge the app on every request.
      setToken(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const signIn = useCallback(
    async (email: string, password: string) => {
      const result = await api.login(email, password)
      setToken(result.access_token)
      await load()
    },
    [load],
  )

  const signOut = useCallback(() => {
    setToken(null)
    setTenantOverride(null)
    setMe(null)
  }, [])

  return { me, loading, error, signIn, signOut, reload: load }
}

export function useHealth() {
  const [health, setHealth] = useState<HealthResponse | null>(null)
  useEffect(() => {
    api
      .health()
      .then(setHealth)
      .catch(() => setHealth(null))
  }, [])
  return health
}

/** Persist a value in localStorage, tolerating private-browsing failures. */
export function usePersistentState<T>(key: string, initial: T) {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key)
      return raw ? (JSON.parse(raw) as T) : initial
    } catch {
      return initial
    }
  })
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value))
    } catch {
      /* nothing to do; the value simply will not persist */
    }
  }, [key, value])
  return [value, setValue] as const
}
